import { useState, useEffect, useRef, useCallback } from "react";
import { AutoComplete, Input } from "antd";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_KEY;
const GEOCODING_URL = "https://api.mapbox.com/geocoding/v5/mapbox.places";

function parseMapboxFeature(feature) {
  const context = feature.context || [];
  const getText = (id) =>
    context.find((c) => c.id.startsWith(id))?.text || "";
  const getShortCode = (id) => {
    const item = context.find((c) => c.id.startsWith(id));
    return item?.short_code || item?.text || "";
  };

  const street = feature.place_type?.includes("address")
    ? [feature.address, feature.text].filter(Boolean).join(" ")
    : feature.text || "";

  const city =
    getText("place") || getText("locality") || getText("district") || "";

  let state = getShortCode("region");
  if (state && state.startsWith("US-")) {
    state = state.replace("US-", "");
  }

  const zipcode = getText("postcode");

  const [longitude, latitude] =
    feature.center || feature.geometry?.coordinates || [];

  return {
    street,
    city,
    state,
    zipcode,
    latitude: latitude ?? null,
    longitude: longitude ?? null,
  };
}

export default function AddressAutocomplete({
  value,
  onChange,
  onAddressSelect,
  placeholder = "Address",
  size,
  inputProps = {},
}) {
  const [inputValue, setInputValue] = useState(value || "");
  const [options, setOptions] = useState([]);

  useEffect(() => {
    if (value !== undefined) {
      setInputValue((prevValue) => (value !== prevValue ? value || "" : prevValue));
    }
  }, [value]);
  const [features, setFeatures] = useState([]);
  const debounceRef = useRef(null);

  const fetchSuggestions = useCallback(async (query) => {
    if (!query || !MAPBOX_TOKEN) {
      setOptions([]);
      setFeatures([]);
      return;
    }

    try {
      const params = new URLSearchParams({
        access_token: MAPBOX_TOKEN,
        types: "address",
        country: "US",
        limit: "7",
        autocomplete: "true",
        proximity: "-118.2437,34.0522",
      });

      const res = await fetch(
        `${GEOCODING_URL}/${encodeURIComponent(query)}.json?${params}`
      );
      const data = await res.json();

      if (data.features) {
        setFeatures(data.features);
        setOptions(
          data.features.map((f) => ({
            value: f.place_name,
            label: f.place_name,
            key: f.id,
          }))
        );
      }
    } catch (err) {
      console.error("Address autocomplete error:", err);
    }
  }, []);

  const handleChange = (newValue) => {
    setInputValue(newValue);
    if (onChange) {
      onChange(newValue);
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 300);
  };

  const handleSelect = (selectedValue) => {
    setInputValue(selectedValue);
    setOptions([]);

    if (onChange) {
      onChange(selectedValue);
    }

    if (onAddressSelect) {
      const feature = features.find((f) => f.place_name === selectedValue);
      if (feature) {
        const parsed = parseMapboxFeature(feature);
        onAddressSelect(parsed);
      }
    }
  };

  return (
    <div onMouseDown={(e) => e.stopPropagation()}>
      <AutoComplete
        value={inputValue}
        options={options}
        onChange={handleChange}
        onSelect={handleSelect}
        getPopupContainer={() => document.body}
        style={{ width: "100%" }}>
        <Input placeholder={placeholder} size={size} {...inputProps} />
      </AutoComplete>
    </div>
  );
}
